import { z } from 'zod/v4-mini';

import { Schema, cz } from '@/utils/zodUtils.ts';

import { TelemonitoringTier, telemonitoringTierSchema } from './CarePlanModel';
import {
  ConditionType,
  MedicationSchema,
  conditionTypeSchema,
  medicationSchemeSchema,
} from './ConditionModel';
import { FileModel, fileSchema } from './FileModel';
import {
  TeleMonitoringCriteria,
  telemonitoringCriteriaSchema,
} from './TelemonitoringCriteriaModel';

export type CarePlanCreationDirectives = {
  periodStart: string;
};

type TelemonitoringCriteriaMapMedication = {
  [key in MedicationSchema]: TeleMonitoringCriteria[];
};

type TelemonitoringCriteriaMap = {
  [key in ConditionType]: TelemonitoringCriteriaMapMedication;
};

type PreviousCarePlanData = {
  telemonitoringCriteria?: TeleMonitoringCriteria;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type TeleMonitoringCriteriaMap = {
  [key in TeleMonitoringCriteria]: TelemonitoringTier; // Gives the tier for each criteria
};

type ConditionData = {
  diabetesType: string;
  insulinType?: string;
  termDate?: string;
};

export type CarePlanDirectives = {
  previousCarePlanData?: PreviousCarePlanData; // Used to pre-fill the renewal form
  previousConditionData?: ConditionData; // Used to pre-fill the renewal form
  carePlanCreationDataDirectives: CarePlanCreationDirectives; // Used to pass data computed by the backend needed to be known by the practitioner (periodStart for example)
  telemonitoringCriteriaMap: TelemonitoringCriteriaMap; // Gives the criteras available for each condition and medication
  telemonitoringTierMap: TeleMonitoringCriteriaMap; // For each criteria, gives the tier
  allowedInsulinScheme: MedicationSchema[]; // The medication schemas allowed for the patient
  allowedDiabetesTypes: ConditionType[]; // The diabetes types allowed for the patient
  restrictions?: TelemonitoringTier;
};

const conditionDataSchema = z.object({
  diabetesType: conditionTypeSchema,
  insulinType: cz.optional(medicationSchemeSchema),
  termDate: cz.optional(z.string()),
});

const previousCarePlanScheme = z.object({
  telemonitoringCriteria: cz.optional(telemonitoringCriteriaSchema),
  description: cz.optional(z.string()),
  descriptionAttachment: cz.optional(fileSchema),
});

export const carePlanDirectivesSchema: Schema<CarePlanDirectives> = z.object({
  previousCarePlanData: cz.optional(previousCarePlanScheme),
  previousConditionData: cz.optional(conditionDataSchema),
  carePlanCreationDataDirectives: z.object({
    periodStart: z.string(),
  }),
  telemonitoringCriteriaMap: cz.unsafe<TelemonitoringCriteriaMap>(),
  telemonitoringTierMap: z.record(
    telemonitoringCriteriaSchema,
    telemonitoringTierSchema,
  ),
  allowedInsulinScheme: z.array(medicationSchemeSchema),
  allowedDiabetesTypes: z.array(conditionTypeSchema),
  restrictions: cz.optional(telemonitoringTierSchema),
});
